import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${(v ?? 0).toFixed(2)} €`;

const Kpi = ({ label, value, accent }) => (
  <Card className="border-border"><CardContent className="py-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold ${accent || ''}`}>{value}</p>
  </CardContent></Card>
);

const GroupTable = ({ title, rows, keyLabel }) => (
  <Card className="border-border">
    <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent className="pt-0">
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground text-left">
          <th className="py-1">{keyLabel}</th><th className="text-right">Cmd</th>
          <th className="text-right">CA</th><th className="text-right">Marge</th><th className="text-right">%</th>
        </tr></thead>
        <tbody>
          {rows.slice(0, 8).map((r) => (
            <tr key={r.key} className="border-t border-border">
              <td className="py-1.5 truncate max-w-[140px]">{r.key}</td>
              <td className="text-right">{r.count}</td>
              <td className="text-right">{eur(r.revenue)}</td>
              <td className={`text-right font-medium ${r.margin < 0 ? 'text-red-600' : ''}`}>{eur(r.margin)}</td>
              <td className="text-right text-muted-foreground">{r.margin_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [labs, setLabs] = useState([]);
  const [cabinets, setCabinets] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/analytics/overview`),
      axios.get(`${API}/admin/analytics/labs`),
      axios.get(`${API}/admin/analytics/cabinets`),
    ]).then(([o, l, c]) => { setData(o.data); setLabs(l.data); setCabinets(c.data); })
      .catch(() => toast.error('Erreur de chargement des statistiques'));
  }, []);

  if (!data) return <AdminLayout title="Pilotage"><div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AdminLayout>;

  return (
    <AdminLayout title="Pilotage métier" subtitle={`Calcul automatique en temps réel · taux USD→EUR : ${data.usd_eur_rate}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5" data-testid="analytics-kpis">
        <Kpi label="Chiffre d'affaires" value={eur(data.revenue)} />
        <Kpi label="Coût fournisseur" value={eur(data.cost)} />
        <Kpi label="Marge brute" value={eur(data.margin)} accent={data.margin < 0 ? 'text-red-600' : 'text-green-600'} />
        <Kpi label="Marge %" value={`${data.margin_pct}%`} />
        <Kpi label="En cours" value={data.in_progress} />
        <Kpi label="Terminées" value={data.completed} />
        <Kpi label="En retard" value={data.late} accent={data.late > 0 ? 'text-orange-600' : ''} />
      </div>
      <Tabs defaultValue="rentabilite">
        <TabsList>
          <TabsTrigger value="rentabilite" data-testid="tab-rentabilite">Rentabilité</TabsTrigger>
          <TabsTrigger value="labs" data-testid="tab-labs">Laboratoires</TabsTrigger>
          <TabsTrigger value="cabinets" data-testid="tab-cabinets">Cabinets</TabsTrigger>
        </TabsList>
        <TabsContent value="rentabilite" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <GroupTable title="Marge par laboratoire" rows={data.by_lab} keyLabel="Laboratoire" />
          <GroupTable title="Marge par cabinet" rows={data.by_cabinet} keyLabel="Cabinet" />
          <GroupTable title="Marge par type de prothèse" rows={data.by_product} keyLabel="Type" />
          <GroupTable title="Marge mensuelle" rows={data.by_month} keyLabel="Mois" />
          <GroupTable title="Marge annuelle" rows={data.by_year} keyLabel="Année" />
          <GroupTable title="Par praticien" rows={data.by_praticien} keyLabel="Praticien" />
          <GroupTable title="Par pays" rows={data.by_country} keyLabel="Pays" />
          <GroupTable title="Par ville" rows={data.by_city} keyLabel="Ville" />
        </TabsContent>
        <TabsContent value="labs">
          <Card className="border-border"><CardContent className="pt-4">
            <table className="w-full text-sm" data-testid="labs-stats-table">
              <thead><tr className="text-muted-foreground text-left text-xs">
                <th className="py-1">Laboratoire</th><th className="text-right">Cmd</th>
                <th className="text-right">Délai réel moy.</th><th className="text-right">Retards</th>
                <th className="text-right">Reprises</th><th className="text-right">Certificats</th>
                <th className="text-right">CA</th><th className="text-right">Marge</th>
              </tr></thead>
              <tbody>{labs.map((l) => (
                <tr key={l.lab} className="border-t border-border">
                  <td className="py-2">{l.lab}</td><td className="text-right">{l.orders}</td>
                  <td className="text-right">{l.avg_real_delay_days ?? '—'} j</td>
                  <td className="text-right">{l.late_rate_pct}%</td>
                  <td className="text-right">{l.reprises}</td><td className="text-right">{l.certificates}</td>
                  <td className="text-right">{eur(l.revenue)}</td>
                  <td className="text-right font-medium">{eur(l.margin)}</td>
                </tr>))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="cabinets">
          <Card className="border-border"><CardContent className="pt-4">
            <table className="w-full text-sm" data-testid="cabinets-stats-table">
              <thead><tr className="text-muted-foreground text-left text-xs">
                <th className="py-1">Cabinet</th><th className="text-right">Cmd</th>
                <th className="text-right">CA</th><th className="text-right">Panier moyen</th>
                <th className="text-right">Praticiens</th><th>Top travaux</th><th>Labo habituel</th>
              </tr></thead>
              <tbody>{cabinets.map((c) => (
                <tr key={c.cabinet} className="border-t border-border">
                  <td className="py-2">{c.cabinet}</td><td className="text-right">{c.orders}</td>
                  <td className="text-right">{eur(c.revenue)}</td>
                  <td className="text-right">{eur(c.avg_basket)}</td>
                  <td className="text-right">{c.praticiens}</td>
                  <td className="text-xs text-muted-foreground">{c.top_products.map(([p, n]) => `${p} (${n})`).join(', ')}</td>
                  <td className="text-xs">{c.usual_lab || '—'}</td>
                </tr>))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
